import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api/axios';
import {PRIMARY_COLOR} from "../contants/Colors";

const EMOJIS = [
  { value: 1, emoji: '😠', label: 'Angry', color: '#ef4444' },
  { value: 2, emoji: '😢', label: 'Sad', color: '#f97316' },
  { value: 3, emoji: '😐', label: 'Neutral', color: '#94a3b8' },
  { value: 4, emoji: '😊', label: 'Happy', color: '#22c55e' },
  { value: 5, emoji: '🤩', label: 'Excited', color: '#8b5cf6' },
];

export default function UserFeedbackPage() {
  const { sessionCode } = useParams();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Audio states
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioDuration, setAudioDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    api.get(`/feedback/session/${sessionCode}`)
      .then(r => { setSession(r.data.data); setLoading(false); })
      .catch(err => {
        setError(err.response?.data?.message || 'Session not found');
        setLoading(false);
      });
  }, [sessionCode]);

  const setAnswer = (questionId, questionText, emojiValue) => {
    setAnswers(prev => ({ ...prev, [questionId]: { questionId, questionText, emojiValue } }));
  };

  const handleManualSubmit = async () => {
    const questions = session?.feedbackForm?.questions || [];
    if (Object.keys(answers).length < questions.length) {
      setError('Please answer all questions before submitting.'); return;
    }
    setError(''); setSubmitting(true);
    try {
      await api.post('/response/submit', { sessionCode, answers: Object.values(answers) });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
      };
      mr.start();
      setRecording(true);
      setAudioDuration(0);
      timerRef.current = setInterval(() => setAudioDuration(d => d + 1), 1000);
    } catch {
      setError('Microphone access denied. Please allow microphone access.');
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
    clearInterval(timerRef.current);
  };

  const handleAudioSubmit = async () => {
    if (!audioBlob) { setError('Please record audio first'); return; }
    setError(''); setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'feedback.webm');
      formData.append('sessionCode', sessionCode);
      formData.append('audioDuration', audioDuration);
      await api.post('/response/submit-audio', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="page flex-center" style={{ minHeight: '100vh' }}>
      <div className="spinner" />
    </div>
  );

  if (error && !session) return (
    <div className="page flex-center" style={{ minHeight: '100vh' }}>
      <div className="card text-center" style={{ maxWidth: 400 }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
        <h3>{error}</h3>
        <p className="text-muted mt-2">This feedback link may be invalid or has already been used.</p>
      </div>
    </div>
  );

  if (submitted) return (
      <div className="page flex-center"
           style={{minHeight: '100vh', background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)', display: 'flex', flexDirection: 'column'}}>
        <div className="card text-center" style={{maxWidth: 440, padding: '48px 40px'}}>
          <div style={{fontSize: 64, marginBottom: 16, animation: 'bounce .5s ease'}}>🎉</div>
          <h2 style={{marginBottom: 12}}>Thank You!</h2>
          <p className="text-muted">Your feedback has been received. We truly appreciate you taking the time!</p>
        </div>
        <div style={{
          fontSize: 11, color: '#94a3b8', marginTop: 20, width: '100%', textAlign: 'center'
        }}>
          Powered by{' '}
          <span>
            <a href={'https://theperceptpulse.com'} target={'_blank'}
               style={{color: PRIMARY_COLOR, textDecoration: 'none'}}>
              Percept Pulse
            </a>
          </span>
        </div>
      </div>
  );

  const isManual = session?.feedbackType === 'manual';
  const questions = session?.feedbackForm?.questions || [];

  return (
      <div className="page"
           style={{minHeight: '100vh', background: 'linear-gradient(135deg, #eff6ff 0%, #f8fafc 100%)'}}>
        <div style={{maxWidth: 600, margin: '0 auto', padding: '40px 20px'}}>
          {/* Header */}
          <div className="card text-center mb-3"
             style={{background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', color: '#fff', border: 'none'}}>
          <div style={{fontSize: 40, marginBottom: 8}}>💬</div>
          <h2 style={{color: '#fff', marginBottom: 4}}>{session?.tenant?.businessName}</h2>
          <p style={{opacity: 0.85, fontSize: 14}}>
            {isManual ? session?.feedbackForm?.title : 'Audio Feedback'} &nbsp;·&nbsp;
            Staff: {session?.feedbackTaker?.name}
            {session?.billId && ` · Bill: ${session.billId}`}
          </p>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        {isManual ? (
            /* ── Manual / Emoji Feedback ── */
            <div>
              {session?.feedbackForm?.description && (
                  <p className="text-muted text-center mb-3"
                     style={{fontSize: 15}}>{session.feedbackForm.description}</p>
              )}
              {questions.map((q, i) => {
                const selected = answers[q._id]?.emojiValue;
                return (
                    <div key={q._id} className="card mb-2">
                      <p style={{fontWeight: 600, marginBottom: 14, fontSize: 15}}>
                        <span style={{color: 'var(--primary)', marginRight: 8}}>Q{i + 1}.</span>{q.text}
                      </p>
                      <div className="emoji-options" style={{justifyContent: 'center'}}>
                        {EMOJIS.map(e => (
                            <button
                                key={e.value}
                                type="button"
                                className={`emoji-btn ${selected === e.value ? 'selected' : ''}`}
                                style={selected === e.value ? {borderColor: e.color, background: e.color + '18'} : {}}
                                onClick={() => setAnswer(q._id, q.text, e.value)}
                            >
                              {e.emoji}
                              <span style={selected === e.value ? {color: e.color} : {}}>{e.label}</span>
                            </button>
                        ))}
                      </div>
                    </div>
                );
              })}
              <button
                  className="btn btn-primary btn-lg"
                  style={{width: '100%', justifyContent: 'center', marginTop: 8}}
                  onClick={handleManualSubmit}
                  disabled={submitting}
              >
                {submitting ? 'Submitting…' : '✅ Submit Feedback'}
              </button>
            </div>
        ) : (
            /* ── Audio Feedback ── */
            <div className="card text-center" style={{padding: '40px 32px'}}>
              <div style={{fontSize: 48, marginBottom: 16}}>🎙️</div>
              <h3 style={{marginBottom: 8}}>Record Your Feedback</h3>
              <p className="text-muted mb-3" style={{fontSize: 14}}>
                Press the button below to start recording. Tell us about your experience!
              </p>

              {audioUrl && !recording && (
                  <div style={{marginBottom: 20}}>
                    <audio controls src={audioUrl} style={{width: '100%', borderRadius: 8}}/>
                    <p style={{fontSize: 12, color: 'var(--text-muted)', marginTop: 6}}>Duration: {audioDuration}s</p>
                  </div>
              )}

              {recording && (
                  <div style={{marginBottom: 20}}>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 10,
                      background: '#fee2e2',
                      borderRadius: 20,
                      padding: '8px 18px',
                      color: '#b91c1c',
                      fontWeight: 600
                    }}>
                      <span style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: '#dc2626',
                        animation: 'pulse 1s infinite'
                      }}/>
                      Recording… {audioDuration}s
                    </div>
                  </div>
              )}

              <div style={{display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap'}}>
                {!recording && !audioBlob && (
                    <button className="btn btn-primary btn-lg" onClick={startRecording}>🎙️ Start Recording</button>
                )}
                {recording && (
                    <button className="btn btn-danger btn-lg" onClick={stopRecording}>⏹ Stop Recording</button>
                )}
                {audioBlob && !recording && (
                    <>
                      <button className="btn btn-outline" onClick={() => {
                        setAudioBlob(null);
                        setAudioUrl(null);
                        setAudioDuration(0);
                      }}>
                        🔄 Re-record
                      </button>
                      <button className="btn btn-primary btn-lg" onClick={handleAudioSubmit} disabled={submitting}>
                        {submitting ? 'Submitting…' : '✅ Submit Audio'}
                      </button>
                    </>
                )}
              </div>
            </div>
        )}
        <div style={{
          fontSize: 11, color: '#94a3b8', marginTop: 20, width: '100%', textAlign: 'center'
        }}>
          Powered by{' '}
          <span>
            <a href={'https://theperceptpulse.com'} target={'_blank'}
               style={{color: PRIMARY_COLOR, textDecoration: 'none'}}>
              Percept Pulse
            </a>
          </span>
        </div>
      </div>
      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }`}</style>
    </div>
  );
}
